import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getStickyStockTableStickyColumnCss } from "utils/reactTableTheme";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
  Modal,
  ModalBody,
} from "reactstrap";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { useGetPortfolios } from "api/portfolios/service";
import { useDeletePortfolio } from "api/portfolios/service";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";

const Portfolios = ({ activeColor = "secondary", account, shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);

  const navigate = useNavigate();
  const { data, isLoading } = useGetPortfolios();
  const deletePortfolioMutation = useDeletePortfolio();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  const handleDeleteClick = useCallback((id) => {
    setSelectedDeleteId(id);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = () => {
    if (selectedDeleteId) {
      deletePortfolioMutation.mutate(selectedDeleteId, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          setSelectedDeleteId(null);
        }
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteId(null);
  };

  const portfolioRows = useMemo(
    () =>
      (data?.results || []).map((portfolio) => ({
        id: portfolio.documentId || portfolio.code,
        documentId: portfolio.documentId,
        code: portfolio.code,
        code_and_name: `${portfolio.code} - ${portfolio.name}`,
        name: portfolio.name,
        type: portfolio.type,
        serviced: portfolio.in_house ? t("portfolios.yes") : t("portfolios.no"),
        activeDisplay: portfolio.active ? t("portfolios.yes") : t("portfolios.no"),
        portfolio,
      })),
    [data?.results, t]
  );

  const portfolioColumns = useMemo(
    () => [
      { Header: t("portfolios.code"), accessor: "code_and_name" },
      { Header: t("portfolios.name"), accessor: "name" },
      { Header: t("portfolios.type"), accessor: "type" },
      { Header: t("portfolios.servicedInHouse"), accessor: "serviced" },
      { Header: t("portfolios.active"), accessor: "activeDisplay" },
      {
        id: "actions",
        Header: "",
        accessor: "documentId",
        disableSortBy: true,
        Cell: ({ row }) => {
          const p = row.original.portfolio;
          return (
            <div className="actions-right" style={{ textAlign: "right" }}>
              {account.permissions.roles_edit && (
                <Button
                  color="link"
                  size="sm"
                  className="btn-icon btn-link like table-edit-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/portfolios/${p.code}`);
                  }}
                >
                  <i className="tim-icons icon-pencil" />
                </Button>
              )}
              {account.permissions.roles_delete && (
                <Button
                  color="link"
                  size="sm"
                  className="btn-icon btn-link like table-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(p.documentId);
                  }}
                  disabled={deletePortfolioMutation.isPending}
                >
                  {deletePortfolioMutation.isPending && selectedDeleteId === p.documentId ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  ) : (
                    <i className="tim-icons icon-simple-remove" />
                  )}
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [
      t,
      account.permissions.roles_delete,
      account.permissions.roles_edit,
      navigate,
      handleDeleteClick,
      deletePortfolioMutation.isPending,
      selectedDeleteId,
    ]
  );

  const PortfoliosTable = shellThemeVariant === "2" ? ReactTable2 : ReactTable;

  return (
    <div
      className={classNames("content", "search-portfolios", {
        "theme2-admin-content-compact": shellThemeVariant === "2",
        "theme2-portfolios-inset": shellThemeVariant === "2",
      })}
    >
      <style>
        {`
          .search-portfolios-card-header.card-header {
            padding-top: 2px;
            padding-bottom: 0;
          }
          .search-portfolios-card-header h2,
          .search-portfolios-card-header h4 {
            font-size: 16px !important;
            margin-bottom: 0;
            line-height: 1.2;
          }
          .search-portfolios .align-items-center {
            margin-top: 3px;
          }
          .search-portfolios .card .card-body {
            padding-top: 0px !important;
            padding-bottom: 0 !important;
            padding-left: 15px;
            padding-right: 15px;
          }
          .sticky-stock-table .rt-table {
            width: 100%;
            min-width: 100%;
            border-collapse: collapse;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child) {
            text-align: center !important;
          }
          .sticky-stock-table .rt-thead .rt-th:last-child {
            text-align: right !important;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child) .rt-resizable-header-content {
            text-align: center;
            width: 100%;
            box-sizing: border-box;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child).-cursor-pointer .rt-resizable-header-content {
            padding-right: 2em;
          }
          .sticky-stock-table .rt-thead .rt-th:last-child .rt-resizable-header-content {
            text-align: right;
            width: 100%;
          }
          .sticky-stock-table .rt-tbody .rt-td:not(:last-child) {
            text-align: center;
            vertical-align: middle;
          }
          .sticky-stock-table .rt-tbody .rt-td:last-child {
            text-align: right;
            vertical-align: middle;
          }
          .sticky-stock-table .rt-tbody .rt-td:not(:last-child) > * {
            max-width: 100%;
            min-width: 0;
          }
            ${getStickyStockTableStickyColumnCss("")}
        `}
      </style>
      <Row>
        <Col md="12">
          <Card>
            <CardHeader className="search-portfolios-card-header">
              <div className="d-flex justify-content-between align-items-center">
                <h2 className="mb-0">{t('portfolios.title')}</h2>
                {account.permissions.roles_add && (
                  <Button
                    size="sm"
                    style={{ ...activeColorStyle, color: 'white' }}
                    onClick={() => navigate('/portfolios/add')}
                  >
                    {t('portfolios.addPortfolio')}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardBody>
              <div
                className="inventory-table-wrap"
                style={{ fontSize: "13px", minWidth: 0, maxWidth: "100%" }}
              >
                <PortfoliosTable
                  activeColor={activeColor}
                  data={portfolioRows}
                  columns={portfolioColumns}
                  className="-striped -highlight sticky-stock-table"
                  manualPagination={false}
                  pageSize={20}
                  loading={isLoading}
                  loadingText={t("portfolios.loading")}
                  noDataText={t("portfolios.noPortfoliosFound")}
                  onRowClick={(row) => {
                    if (row?.code) navigate(`/portfolios/${row.code}`);
                  }}
                />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Modal isOpen={deleteModalOpen} toggle={handleCancelDelete} centered={false} style={{ marginTop: '100px' }}>
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">{t('portfolios.deletePortfolio')}</h6>
        </div>
        <ModalBody className="text-center">
          <p>{t('portfolios.deleteConfirmation')}</p>
        </ModalBody>
        <div className="modal-footer">
          <Button
            color="secondary"
            type="button"
            onClick={handleCancelDelete}
            disabled={deletePortfolioMutation.isPending}
          >
            {t('portfolios.cancel')}
          </Button>
          <Button
            style={{
              background: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
              backgroundImage: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
              borderColor: '#fd5d93',
              color: 'white'
            }}
            color="danger"
            type="button"
            onClick={handleConfirmDelete}
            disabled={deletePortfolioMutation.isPending}
          >
            {deletePortfolioMutation.isPending ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                {t('portfolios.deleting')}
              </>
            ) : (
              t('portfolios.yesDelete')
            )}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Portfolios;
